import * as React from "react";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { SvcResults, StudySvcRelevantParameters } from "../Shared/PFTAPITypes";
import { ToggleTestGraphVisibilityAction } from '../Shared/UseGraphs';
import { FixedCommentsState, PageValidatedAction } from "../../PulmonaryFunctionTests/Shared/UseFixedComments";
import { SvcGraphState } from './Graphing/UseSvcGraphs'
import { CommentModals } from "../Shared/CommentsModals";
import ResultsCommentsTable from "../Shared/ResultsCommentsTable";
import { SVCResultParameters } from "./SVCResultsParameters";
import RepeatabilityTable from "../Shared/RepeatabilityTable";
import { SvcQualityAcceptabilityTable } from "./SvcQualityAcceptabilityTable";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from '../Shared/UseAcceptability';
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { SvcTestAcceptabilityTable } from "./SvcTestAcceptabilityTable";

export interface SvcResultsTableProps {
    analysisSetId: string;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    siteAnalysisSetId: string;
    isViewOnly: boolean;
    resultData: SvcResults;
    fixedCommentsState: FixedCommentsState;
    fixedCommentsDispatch;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    graphState: SvcGraphState;
    technicianSessionCommentsAvailable: boolean;
    technicianTestCommentsAvailable: boolean;
    graphDispatch;
    studySvcRelevantParameters?: StudySvcRelevantParameters;
}

export const SvcResultsTable = (props: SvcResultsTableProps) => {
    if (props.resultData !== null) {
        return (
            <div style={{ overflow: "scroll", maxWidth: "100%" }}>
                <table id="ResultsTable" className="table table-striped">
                    <thead>
                        <tr>
                            <th />
                            {props.graphState.flow.Tests.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                <th
                                    key={`TestHeader${t.TestId}`}
                                    style={{ background: t.ColorHex, color: "#fff" }}>
                                    {t.TestNumber}
                                </th>
                            ))}
                            <th>Best</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Display</td>
                            {props.graphState.flow.Tests.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                <td key={`TestVisibilityToggle${t.TestId}`}>
                                    <input
                                        checked={t.IsDisplayed}
                                        type="checkbox"
                                        data-automationid={`svc-results-table-toggle-test-${t.TestNumber}`}
                                        onChange={() =>
                                            props.graphDispatch(
                                                ToggleTestGraphVisibilityAction.create(t.TestId)
                                            )
                                        }
                                    />
                                </td>
                            ))}
                            <td />
                        </tr>
                        <tr>
                            <td>Time</td>
                            {props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                <td key={`TestDateTime${t.TestId}`}>
                                    <LocalizedDateTime
                                        time={t.TestDateTime}
                                        accuracy={DateTimeAccuracy.Second}
                                        automationId={ `test-datetime-${t.TestNumber}` }
                                    />
                                </td>
                            ))}
                            <td />
                        </tr>                 
                        <ResultsCommentsTable
                            addCommentsSuffix={false}
                            siteAnalysisSetId={props.siteAnalysisSetId}
                            resultData={props.resultData}
                            fixedCommentsState={props.fixedCommentsState}
                            fixedCommentsDispatch={props.fixedCommentsDispatch}
                            technicianSessionCommentsAvailable={props.technicianSessionCommentsAvailable}
                            technicianTestCommentsAvailable={props.technicianTestCommentsAvailable}
                            sessionType={SessionTypes.Svc}
                        />
                        <SVCResultParameters
                            isLoading={props.acceptabilityState.loadingBestValues}
                            results={props.resultData}
                            isRejected={props.isRejected}
                            studySvcRelevantParameters={props.studySvcRelevantParameters}
                            showConfiguredParametersOnly={true}
                        />
                        <RepeatabilityTable
                            isLoading={props.acceptabilityState.loadingBestValues}
                            results={props.resultData}
                            isRejected={props.isRejected}
                            studyRelevantParameters={props.studySvcRelevantParameters}
                            showConfiguredParametersOnly={true}
                        />
                        <SvcQualityAcceptabilityTable results={props.resultData} />
                        <SvcTestAcceptabilityTable
                            isLoading={props.acceptabilityState.loadingBestValues}
                            isViewOnly={props.isViewOnly}
                            testResults={props.acceptabilityState.spirometryResults.TestResults}
                            displayTechnicianAcceptability={!props.isSiteAnalysisSet}
                            onAcceptabilityChange={(id, checked) =>
                                props.acceptabilityDispatch(
                                    ToggleTestAcceptabilityAction.create(id, checked)
                                )
                            }
                        />
                        <tr>
                            <td>Site Software Interpretation</td>
                            {props.resultData.TestResults.map(tr => {
                                return <td key={`SoftwareInterpretation${tr.TestId}`}>N/A</td>;
                            })}
                            <td>{props.resultData.SoftwareInterpretation}</td>
                        </tr>
                        <CommentModals
                            editable={props.fixedCommentsState.areFixedCommentsLoaded && !props.isViewOnly}
                            sessionFixedComments={props.fixedCommentsState.sessionFixedComments}
                            testFixedComments={props.fixedCommentsState.testFixedComments}
                            setIsPageValidated={(isValidated) => props.fixedCommentsDispatch(PageValidatedAction.create(isValidated))}
                            isPageValid={props.fixedCommentsState.isPageValidated}
                            dispatch={props.fixedCommentsDispatch}
                        />
                    </tbody>
                </table>
            </div>
        );
    } else {
        return <LoadingWithMessage message="Loading Spirometry Results..." />;
    }
}

export default SvcResultsTable;
